/**
 * useAudioGift Hook - Audio Gift Session Creation
 *
 * Creates personalized meditation sessions as gifts for loved ones.
 * Uses the recipient's biography from the biography library for personalization.
 *
 * Flow:
 * 1. User selects a biography from their library
 * 2. User selects category and fills questionnaire
 * 3. Session is generated using the recipient's biography
 * 4. Share link is created for the recipient
 */

import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/utils/apiClient';
import { toast } from 'sonner';

// Express gift data structure for quick gift creation without biography
export interface ExpressGiftData {
  recipientName: string;
  occasion: string;
  customOccasion?: string;
  relationship?: string;
  currentSituation?: string;
  specialDetails?: string;
}

interface CreateGiftSessionParams {
  // Standard mode: use biography from library
  recipientBiographyId?: string;  // Optional for express mode
  categoryId?: string;  // Optional for express mode (auto-selected)
  title: string;
  sessionLength: number; // Session length in seconds
  voiceGender: string;
  specificVoiceId?: string;
  giftMessage?: string;
  senderName: string;
  recipientEmail?: string;
  questionnaireData?: Record<string, any>;
  backgroundMusicFile?: string;
  // Biography fields - optional for express mode
  recipientBiographyText?: string;  // Optional for express mode
  recipientFirstName?: string;
  recipientPreferredName?: string;
  recipientNameUsagePreference?: string;
  // Express gift mode - quick creation without biography library
  expressMode?: boolean;
  expressGiftData?: ExpressGiftData;
  // Custom text mode - flexible text integration options
  // Modes: 'verbatim' (skip AI, use directly), 'blend_beginning', 'blend_end',
  //        'blend_middle', 'blend_woven', 'blend_custom'
  customTextMode?: 'verbatim' | 'blend_beginning' | 'blend_end' | 'blend_middle' | 'blend_woven' | 'blend_custom';
  customText?: string;
  customTextInstructions?: string;
  // Legacy fields (for backwards compatibility)
  useLiteralScript?: boolean;
  literalScript?: string;
}

interface GiftSessionResult {
  sessionId: string;
  shareLink: string;
  jobId?: string;
  audioUrl?: string;
  recipientEmail?: string;
}

interface SendGiftEmailParams {
  sessionId: string;
  recipientEmail: string;
  giftMessage?: string; // Optional override for the gift message
}

interface SendGiftEmailResult {
  success: boolean;
  recipient_email: string;
  message: string;
}

interface GiftEmailPreviewParams {
  sessionId: string;
  recipientEmail?: string;
  giftMessage?: string;
}

interface GiftEmailPreviewResult {
  html: string;
  subject: string;
  data: {
    sender_name: string;
    gift_message: string | null;
    recipient_email: string;
    share_url: string;
  };
}

export const useAudioGift = () => {
  const createGiftSession = useMutation({
    mutationFn: async (params: CreateGiftSessionParams): Promise<GiftSessionResult> => {
      // 1. Generate the meditation session using the recipient's biography
      // Extract category-specific fields (like explicitLanguageLevel) for backend processing
      // Backend expects these in category_specific_data, not questionnaire_data
      const qData = params.questionnaireData || {};
      const categorySpecificData = {
        // CRITICAL: explicitLanguageLevel must be a number for proper prompt formatting
        // NOTE: The default should come from the category config (zen-af: 8, news-meditation: 5)
        // The frontend (SendAudioGiftPage.tsx) should always provide this value from category config.
        // This fallback is only a safety net for unexpected cases.
        explicitLanguageLevel: qData.explicitLanguageLevel
          ? parseInt(String(qData.explicitLanguageLevel), 10)
          : undefined, // Let backend handle missing values - frontend should always provide from category config
        humorLevel: qData.humorLevel,
        specificFocus: qData.specificFocus,
        deeperReason: qData.deeperReason,
        primaryGoal: qData.primaryGoal,
        preferredStyle: qData.preferredStyle,
        currentNews: qData.currentNews,
        newsConsumption: qData.newsConsumption,
        additionalNotes: qData.additionalNotes,
      };

      const meditationResponse = await apiClient.post('/meditation/generate', {
        form_type: 'personalized',
        category_id: params.categoryId,
        title: params.title,
        environment: 'relaxing',
        voice_gender: params.voiceGender,
        specific_voice_id: params.specificVoiceId,
        meditation_style: 'gentle',
        voice_style: 'calming',
        seeking_goal: 'relaxation',
        session_length: params.sessionLength,
        desired_feeling: 'peaceful',
        include_background_sounds: !!params.backgroundMusicFile,
        background_music_file: params.backgroundMusicFile || null,
        // Use the recipient's biography for personalization (not used in express mode)
        biography: params.recipientBiographyText,
        first_name: params.recipientFirstName,
        // Gift recipient email - stored for manual email sending via sendGiftEmail()
        gift_recipient_email: params.recipientEmail || null,
        // EXPRESS GIFT MODE - quick creation without biography library
        express_mode: params.expressMode || false,
        express_gift_data: params.expressGiftData || null,
        // CRITICAL: Send category-specific data (including explicitLanguageLevel) for backend processing
        category_specific_data: categorySpecificData,
        // Pass questionnaire data for additional personalization
        // Include custom text fields for blend modes (AI will see these in questionnaire_data)
        questionnaire_data: {
          ...params.questionnaireData,
          is_gift: true,
          sender_name: params.senderName,
          gift_message: params.giftMessage,
          recipient_biography_id: params.recipientBiographyId,
          // Include custom text fields for AI blending (non-verbatim modes)
          ...(params.customTextMode && params.customTextMode !== 'verbatim' ? {
            customTextMode: params.customTextMode,
            customText: params.customText,
            customTextInstructions: params.customTextInstructions,
          } : {}),
        },
        // Custom text mode - flexible text integration options
        custom_text_mode: params.customTextMode || null,
        custom_text: params.customText || null,
        custom_text_instructions: params.customTextInstructions || null,
        // Legacy fields (for backwards compatibility)
        use_literal_script: params.useLiteralScript || (params.customTextMode === 'verbatim'),
        literal_script: params.useLiteralScript ? params.literalScript : (params.customTextMode === 'verbatim' ? params.customText : null),
      });

      const jobId = meditationResponse.id;

      if (!jobId) {
        throw new Error('No job ID returned from meditation generation');
      }

      // 2. Poll for job completion
      let session = null;
      let attempts = 0;
      const maxAttempts = 60; // 5 minutes with 5 second intervals

      while (!session && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        attempts++;

        const jobStatus = await apiClient.get(`/jobs/job/${jobId}`);

        if (jobStatus.status === 'completed') {
          // Get the session from result_data
          if (jobStatus.result_data?.session_id) {
            const sessionResponse = await apiClient.get(
              `/sessions/${jobStatus.result_data.session_id}`
            );
            session = sessionResponse;
          }
          break;
        } else if (jobStatus.status === 'failed') {
          throw new Error(
            jobStatus.error_message || 'Gift session generation failed'
          );
        }
      }

      if (!session) {
        throw new Error('Gift session generation timed out');
      }

      // 3. Get the share link for the session (worker already created it)
      // Use GET to retrieve existing share link without triggering premix generation
      const shareResponse = await apiClient.get(`/sessions/${session.id}/share`);

      // Verify share link was created (worker should have created it)
      if (!shareResponse.share_url) {
        throw new Error('Share link not available. Please try again.');
      }

      // Note: Email notification is sent manually from the frontend
      // after user previews the audio and confirms

      // Determine the audio URL (prefer mixed_audio_url if available)
      const audioUrl = session.mixed_audio_url || session.audio_url;

      return {
        sessionId: session.id,
        shareLink: shareResponse.share_url,
        jobId,
        audioUrl,
        recipientEmail: params.recipientEmail,
      };
    },
    onSuccess: () => {
      toast.success('Gift session created successfully!');
    },
    onError: (error: Error) => {
      console.error('Gift creation error:', error);
      toast.error(error.message || 'Failed to create gift session');
    },
  });

  // Mutation to send gift email manually
  const sendGiftEmail = useMutation({
    mutationFn: async (params: SendGiftEmailParams): Promise<SendGiftEmailResult> => {
      const response = await apiClient.post(
        `/sessions/${params.sessionId}/send-gift-email`,
        {
          recipient_email: params.recipientEmail,
          gift_message: params.giftMessage,
        }
      );
      return response;
    },
    onSuccess: (data) => {
      toast.success(`Gift email sent to ${data.recipient_email}`);
    },
    onError: (error: Error) => {
      console.error('Gift email error:', error);
      toast.error(error.message || 'Failed to send gift email');
    },
  });

  // Mutation to get email preview
  const getGiftEmailPreview = useMutation({
    mutationFn: async (params: GiftEmailPreviewParams): Promise<GiftEmailPreviewResult> => {
      const response = await apiClient.post(
        `/sessions/${params.sessionId}/gift-email-preview`,
        {
          recipient_email: params.recipientEmail,
          gift_message: params.giftMessage,
        }
      );
      return response;
    },
    onError: (error: Error) => {
      console.error('Gift email preview error:', error);
      toast.error(error.message || 'Failed to load email preview');
    },
  });

  return { createGiftSession, sendGiftEmail, getGiftEmailPreview };
};

export default useAudioGift;
